import type { Command } from "../document/commands.ts";
import type { DoorCommand } from "../document/door-commands.ts";
import { MAX_DOORS_PER_LAYER, doorState, type DoorCellChange, type DoorState } from "../document/doors.ts";
import { sameJson } from "../document/same-json.ts";
import type { DoorDefinition, DoorLayer, DoorTarget, JsonValue, WorldDocument } from "../document/types.ts";
import { hasOnly, isId, isName, jsonRecordOf, keyChangesOf, type Fields } from "./op-read.ts";
import type { Intent, IntentOf, KeyChanges } from "./op-types.ts";
import { layerOf, mapOf, passesFieldLimits, type Resolution } from "./resolve-types.ts";

/**
 * Doors under live editing (ADR-122 decision 2.1; its note "how P1.4-T137 is built", item 8): how `addDoor`,
 * `removeDoor` and `updateDoor` read from a frame, how each resolves against the document a replica holds, and
 * how a door command and a door layer's content travel as intents. A door's cells travel as the cell patch's
 * `door` grid, its values door ids. An add is placed after a neighbour's id; a removal clears every cell still
 * naming the door; an update writes the name, the destination and each key of the fields apart, so two builders
 * changing different keys both land.
 */

/**
 * The door intents.
 */
export type DoorIntent = IntentOf<"addDoor"> | IntentOf<"removeDoor"> | IntentOf<"updateDoor">;

/**
 * Whether a value is a door's or a destination's name an operation carries: a name within its length, not blank.
 *
 * @param value The value.
 * @returns True for such a string.
 */
function isFilledName(value: unknown): value is string {
  return isName(value) && value.trim() !== "";
}

/**
 * Reads a door's destination.
 *
 * @param value The value.
 * @returns The destination, or undefined when it is not exactly a map's id and an arrival point's id.
 */
function targetOf(value: unknown): DoorTarget | undefined {
  const fields = typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Fields) : undefined;
  if (fields === undefined || !hasOnly(fields, ["map", "arrival"]) || !isId(fields["map"]) || !isId(fields["arrival"])) {
    return undefined;
  }
  return { map: fields["map"], arrival: fields["arrival"] };
}

/**
 * Reads a door.
 *
 * @param value The value.
 * @returns The door, or undefined when it is not exactly an id, a name that is not blank, a destination and a
 *   record of fields.
 */
function doorOf(value: unknown): DoorDefinition | undefined {
  const fields = typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Fields) : undefined;
  if (fields === undefined || !hasOnly(fields, ["id", "name", "to", "fields"]) || !isId(fields["id"]) || !isFilledName(fields["name"])) {
    return undefined;
  }
  const to = targetOf(fields["to"]);
  const record = jsonRecordOf(fields["fields"]);
  return to === undefined || record === undefined ? undefined : { id: fields["id"], name: fields["name"], to, fields: record };
}

/**
 * Reads a door intent.
 *
 * @param fields The intent's fields.
 * @returns The intent, or undefined when a field is missing, extra or mistyped, or an update names nothing to
 *   change.
 */
export function doorIntentOf(fields: Fields): Intent | undefined {
  const { map, layer, after } = fields;
  if (!isId(map) || !isId(layer)) {
    return undefined;
  }
  switch (fields["k"]) {
    case "addDoor": {
      const door = hasOnly(fields, ["k", "map", "layer", "door", "after"]) && (after === null || isId(after)) ? doorOf(fields["door"]) : undefined;
      return door === undefined ? undefined : { k: "addDoor", map, layer, door, after: after as string | null };
    }
    case "removeDoor":
      return hasOnly(fields, ["k", "map", "layer", "door"]) && isId(fields["door"]) ? { k: "removeDoor", map, layer, door: fields["door"] } : undefined;
    case "updateDoor": {
      const { door, name } = fields;
      const to = fields["to"] === undefined ? null : targetOf(fields["to"]);
      const changes = hasOnly(fields, ["k", "map", "layer", "door"], ["name", "to", "set", "remove"]) && isId(door) ? keyChangesOf(fields) : undefined;
      if (changes === undefined || to === undefined || (name !== undefined && !isFilledName(name)) || (name === undefined && to === null && changes.set === undefined && changes.remove === undefined)) {
        return undefined;
      }
      return { k: "updateDoor", map, layer, door: door as string, ...(name === undefined ? {} : { name: name as string }), ...(to === null ? {} : { to }), ...changes };
    }
    default:
      return undefined;
  }
}

/**
 * The door layer an intent names.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The layer, or the reason the intent is skipped: `gone` or `kind`.
 */
function doorLayerOf(world: WorldDocument, intent: DoorIntent): DoorLayer | "gone" | "kind" {
  const layer = layerOf(world, intent.map, intent.layer);
  if (layer === undefined) {
    return "gone";
  }
  return layer.kind === "door" ? layer : "kind";
}

/**
 * A record with keys set and removed, its keys in order so equal records are equal text.
 *
 * @param before The record as held.
 * @param changes The keys to set and to remove.
 * @returns The new record.
 */
function withKeys(before: { readonly [key: string]: JsonValue }, changes: KeyChanges): { readonly [key: string]: JsonValue } {
  const next = new Map<string, JsonValue>(Object.entries(before));
  for (const [key, value] of Object.entries(changes.set ?? {})) {
    next.set(key, value);
  }
  for (const key of changes.remove ?? []) {
    next.delete(key);
  }
  return Object.fromEntries([...next].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
}

/**
 * Resolves a door's add by id and neighbour.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the door is already there as carried, or the reason: `gone`, `kind`,
 *   `taken` when another door of the map holds its id, `limit` for a full layer or fields past their limits.
 */
function resolveAddDoor(world: WorldDocument, intent: IntentOf<"addDoor">): Resolution {
  const layer = doorLayerOf(world, intent);
  if (typeof layer === "string") {
    return layer;
  }
  const held = layer.doors.find((door) => door.id === intent.door.id);
  if (held !== undefined) {
    return sameJson(held, intent.door) ? [] : "taken";
  }
  const map = mapOf(world, intent.map);
  if (map?.layers.some((other) => other.kind === "door" && other.doors.some((door) => door.id === intent.door.id))) {
    return "taken";
  }
  if (layer.doors.length >= MAX_DOORS_PER_LAYER || passesFieldLimits(undefined, intent.door.fields)) {
    return "limit";
  }
  const at = intent.after === null ? 0 : layer.doors.findIndex((door) => door.id === intent.after) + 1 || layer.doors.length;
  return [{ kind: "addDoor", mapId: intent.map, layerId: layer.id, index: at, door: intent.door }];
}

/**
 * Resolves a door's removal: every cell still naming the door cleared first, then the door.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The commands, nothing when the door or its layer is already gone, or `kind`.
 */
function resolveRemoveDoor(world: WorldDocument, intent: IntentOf<"removeDoor">): Resolution {
  const layer = doorLayerOf(world, intent);
  if (layer === "gone") {
    return [];
  }
  if (layer === "kind") {
    return "kind";
  }
  const index = layer.doors.findIndex((door) => door.id === intent.door);
  const door = layer.doors[index];
  if (door === undefined) {
    return [];
  }
  const at = { mapId: intent.map, layerId: layer.id };
  const cells: DoorCellChange[] = [];
  for (let i = 0; i < layer.cells.length; i += 1) {
    if (layer.cells[i] === index + 1) {
      cells.push({ index: i, before: door.id, after: null });
    }
  }
  const removal: Command = { kind: "removeDoor", ...at, index, door };
  return cells.length === 0 ? [removal] : [{ kind: "paintDoor", ...at, cells }, removal];
}

/**
 * Resolves a door's update: its name and destination when carried, and each key of its fields.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the door already holds what the intent writes, or the reason: `gone` (the
 *   layer or the door), `kind`, `limit` for fields past their limits.
 */
function resolveUpdateDoor(world: WorldDocument, intent: IntentOf<"updateDoor">): Resolution {
  const layer = doorLayerOf(world, intent);
  if (typeof layer === "string") {
    return layer;
  }
  const door = layer.doors.find((candidate) => candidate.id === intent.door);
  if (door === undefined) {
    return "gone";
  }
  const before = doorState(door);
  const fields = intent.set === undefined && intent.remove === undefined ? door.fields : withKeys(door.fields, intent);
  const after: DoorState = { name: intent.name ?? door.name, to: intent.to ?? door.to, fields };
  if (sameJson(before, after)) {
    return [];
  }
  return passesFieldLimits(door.fields, fields) ? "limit" : [{ kind: "updateDoor", mapId: intent.map, layerId: layer.id, doorId: door.id, before, after }];
}

/**
 * Resolves a door intent.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns Its commands, none when it changes nothing, or the reason it is skipped.
 */
export function resolveDoorIntent(world: WorldDocument, intent: DoorIntent): Resolution {
  switch (intent.k) {
    case "addDoor":
      return resolveAddDoor(world, intent);
    case "removeDoor":
      return resolveRemoveDoor(world, intent);
    case "updateDoor":
      return resolveUpdateDoor(world, intent);
  }
}

/**
 * A door as an operation carries it.
 *
 * @param door The door.
 * @returns The door with exactly the fields the codec reads.
 */
function doorWire(door: DoorDefinition): DoorDefinition {
  return { id: door.id, name: door.name, to: { map: door.to.map, arrival: door.to.arrival }, fields: door.fields };
}

/**
 * The keys that differ between two records.
 *
 * @param before The record as it was.
 * @param after The record as it is.
 * @returns The keys to set and to remove, each part present only when it holds a key.
 */
function keyChanges(before: { readonly [key: string]: JsonValue }, after: { readonly [key: string]: JsonValue }): KeyChanges {
  const set = Object.fromEntries(Object.entries(after).filter(([key, value]) => !Object.hasOwn(before, key) || !sameJson(before[key], value)));
  const remove = Object.keys(before).filter((key) => !Object.hasOwn(after, key));
  return { ...(Object.keys(set).length === 0 ? {} : { set }), ...(remove.length === 0 ? {} : { remove }) };
}

/**
 * The intents that add a door layer's doors, each after the one before it; its cells follow as a patch.
 *
 * @param mapId The map.
 * @param layer The layer.
 * @returns The intents.
 */
export function doorListIntents(mapId: string, layer: DoorLayer): Intent[] {
  return layer.doors.map((door, i) => ({ k: "addDoor", map: mapId, layer: layer.id, door: doorWire(door), after: layer.doors[i - 1]?.id ?? null }));
}

/**
 * The intents of a door command other than a stroke, which travels as a cell patch.
 *
 * @param command The command.
 * @param layer The door layer before it, for the neighbour an added door goes after.
 * @returns The intents, none when the command changes nothing.
 */
export function doorCommandIntents(command: Exclude<DoorCommand, { readonly kind: "paintDoor" }>, layer: DoorLayer | undefined): Intent[] {
  const at = { map: command.mapId, layer: command.layerId };
  switch (command.kind) {
    case "addDoor":
      return [{ k: "addDoor", ...at, door: doorWire(command.door), after: layer?.doors[command.index - 1]?.id ?? null }];
    case "removeDoor":
      return [{ k: "removeDoor", ...at, door: command.door.id }];
    case "updateDoor": {
      const { before, after } = command;
      const changes = keyChanges(before.fields, after.fields);
      const named = before.name === after.name ? {} : { name: after.name };
      const to = sameJson(before.to, after.to) ? {} : { to: { map: after.to.map, arrival: after.to.arrival } };
      if (Object.keys(named).length === 0 && Object.keys(to).length === 0 && changes.set === undefined && changes.remove === undefined) {
        return [];
      }
      return [{ k: "updateDoor", ...at, door: command.doorId, ...named, ...to, ...changes }];
    }
  }
}
